import React from "react";
import {ResponsiveLine, Serie} from '@nivo/line'
import tailwindConfig from "../../tailwind.config";
import {Body, Heading2, Paragraph} from "../text";
import {useAppSelector} from "../../app/hooks";
import {selectEndDate, selectSelectedTab, selectStartDate} from "../../redux/affiliate/affiliateSlice";
import Moment from "moment"
import {CurrencyCode} from "../../models/currencyCode";
import {CurrencyUtils} from "../../utils/currencyUtils";

export interface AffiliateDashboardChartProps {
  title: string
  isLoading: boolean
  className?: string
  currencyCode?: CurrencyCode
  data: Serie[]
}

export const AffiliateDashboardChart: React.FC<AffiliateDashboardChartProps> = ({ className, title, isLoading, data, currencyCode }) => {
  const startDate = useAppSelector(selectStartDate)
  const endDate = useAppSelector(selectEndDate)
  const tab = useAppSelector(selectSelectedTab)

  const startDateString = Moment(startDate).format('MMM D, YYYY')
  const endDateString = Moment(endDate).format('MMM D, YYYY')

  const xPointCount = data[0]?.data?.length ?? 0
  const tickValues = `every ${Math.round(xPointCount / 10)} days`

  return <div className={` ${className} p-6 rounded-lg border border-fg-gray-surface`}>
    <Heading2 className="mb-2">{title}</Heading2>
    <Paragraph className="mb-6 text-fg-gray-dark">{`${startDateString} – ${endDateString}`}</Paragraph>

    <div className={`relative h-screen-1/3 max-h-96 w-full min-h-48`}>
      <ResponsiveLine
        data={data}
        margin={{
          top: 40,
          right: 50,
          bottom: 80,
          left: 50
        }}
        xFormat={(value) => {
          return Moment((value as Date)).format('MMM D')
        }}
        yScale={{
          type: "linear",
          min: "auto",
          max: "auto",
          stacked: false,
          reverse: false
        }}
        xScale={{ type: 'time', format: '%s' }}
        yFormat={value => {
          if (typeof value === 'number') {
            return CurrencyUtils.stringFromPrice(value, currencyCode)
          } else {
            return value.toString()
          }
        }}
        axisTop={undefined}
        axisRight={undefined}
        axisBottom={{
          format: (value) => {
            return Moment((value as Date)).format('MMM D')
          },
          tickValues: tickValues,
          "tickPadding": 5,
          "tickRotation": 0,
          "legendPosition": "middle"
        }}
        axisLeft={{
          "tickSize": 5,
          tickValues: 5,
          "tickPadding": 5,
          "tickRotation": 0,
          "legend": "",
          "legendOffset": -40,
          "legendPosition": "middle"
        }}
        enableGridX={false}
        pointSize={8}
        pointBorderWidth={2}
        pointBorderColor="#ffffff"
        animate={true}
        colors={[ '#008BCE', '#FAD337', '#21AE5F', '#FF5C40', '#031926']}
        enableSlices="x"
        sliceTooltip={({ slice }) => {
          return <div className="flex flex-col space-y-3 p-2 bg-white shadow-lg rounded-lg ring-1 ring-fg-gray-surface absolute bottom-0 left-3">
            {slice.points.map((point) => {
              const key = tab === 'store_credit_payouts'
                ? (point.serieId as string).split('|')[1]
                : Moment(point.data.x as Date).format('MMM D, YYYY')
              return <div key={point.id} className="items-center flex space-x-2">
                <div className="h-4 w-4" style={{ backgroundColor: point.serieColor }} />
                <Body className="whitespace-nowrap">{key}: <span className="font-semibold">{point.data.yFormatted}</span></Body>
              </div>
            })}
          </div>
        }}
        theme={{
          legends: {
            text: {
              fontSize: '1rem',
              fontWeight: 400,
              fontFamily: 'Gilroy',
              fill: tailwindConfig.theme.colors['fg-black'],
            },
          },
          axis: {
            ticks: {
              text: {
                fontSize: '1rem',
                fontWeight: 400,
                fontFamily: 'Gilroy',
                fill: tailwindConfig.theme.colors['fg-black'],
              },
            },
          },
          grid: {
            line: {
              stroke: tailwindConfig.theme.colors['fg-gray-surface-light'],
              strokeWidth: 1,
            },
          },
        }}
        legends={[]}
      />
    </div>

  </div>
}